import { useState, createContext, useEffect, useContext } from "react";
import { API } from "aws-amplify";
import { useAuth } from "./Auth";
import { PairingColumns } from "../models/PairingInterface";
import { useApiError } from "./ApiErrorProvider";
import { error } from "../constants/bindings";
import { GridSortModel } from "@mui/x-data-grid-pro";

const columnsDefault = { columns: [] };
const presetsDefault = { presetNames: [] };

const ColumnContext = createContext({
  loading: true,
  savePresets: (presetData, presetName) => {},
  saveSortPreferenceData: (sortModel: GridSortModel) => {},
  getColumnPreferences: (presetName?) => {},
  getColumnPresets: () => {},
  columnPreferences: columnsDefault as PairingColumns,
  columnPresets: presetsDefault,
});

export const ColumnPresetsProvider = (props) => {
  const [loading, setLoading] = useState(true);
  const [columnPreferences, setColumnPreferences] =
    useState<any>(columnsDefault);
  const [columnPresets, setColumnPresets] = useState(presetsDefault);
  const { user } = useAuth();
  const { setErrorCode } = useApiError();

  useEffect(() => {
    if (!user) {
      return;
    }
    getColumnPreferences();
    getColumnPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const getColumnPreferences = async (presetName: string = "default") => {
    if (!user) {
      return;
    }
    try {
      setLoading(true);
      const columnPreferenceData = await API.get(
        "crewlife",
        `/presetpreference/retrieve/pairingreport/${presetName}`,
        {}
      );

      /**
       * When the user loads a preset and it is not the default preset,
       * grab the sort preference data from default because that is where sort preferences are saved.
       * Save this preset into the default preset as the 'default' preset will always return on page load/return
       * done to simulate 'last viewed' which is always the 'default' preset
       */
      if (presetName !== "default") {
        const sortPreferenceData = await API.get(
          "crewlife",
          `/presetpreference/retrieve/pairingreport/default`,
          {}
        );
        const requestObject = {
          body: {
            ...columnPreferenceData,
            sortOrder: sortPreferenceData.sortOrder,
            presetName: "default",
          },
        };
        await API.post("crewlife", "/presetpreference/save", requestObject);
        setColumnPreferences({
          ...columnPreferenceData,
          sortOrder: sortPreferenceData.sortOrder,
          presetName: "default",
        });
      } else {
        setColumnPreferences({ ...columnPreferenceData });
      }
      setLoading(false);
    } catch (e: any) {
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const getColumnPresets = async () => {
    if (!user) {
      return;
    }
    try {
      setLoading(true);
      const columnPresetData = await API.get(
        "crewlife",
        "/presetpreference/list",
        {}
      );
      const indexOfDefault = columnPresetData.presetNames.findIndex(
        (name) => name === "default"
      );
      if (indexOfDefault !== -1)
        columnPresetData.presetNames.splice(indexOfDefault, 1);
      setColumnPresets(columnPresetData);
      setLoading(false);
    } catch (e: any) {
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const savePresets = async (presetData, presetName) => {
    if (!user || !presetData) {
      return;
    }
    let columnsToSave = [{}]; // after fix the PairingData interface, can add here
    columnPreferences.columns.forEach((val) => {
      let tempObj = {
        internalCode: val.internalCode,
        defaultDisplay: val.defaultDisplay,
        preferredDisplay: presetData[val.internalCode],
      };
      columnsToSave.push(tempObj);
    });
    columnsToSave.shift(); // remove it, after PairingData interface added
    try {
      /**
       * Get the updated/correct sort preferences as they are always saved into the 'default' preset
       * and save them into which ever preset is currently in use
       */
      const sortPreferenceData = await API.get(
        "crewlife",
        `/presetpreference/retrieve/pairingreport/default`,
        {}
      );
      let screenName = columnPreferences.screenName || "pairingreport";
      setLoading(true);
      const requestObject = {
        body: {
          presetName,
          screenName,
          columns: columnsToSave,
          sortOrder: sortPreferenceData.sortOrder,
        },
      };
      await API.post("crewlife", "/presetpreference/save", requestObject);
      getColumnPresets(); /// TODO SHOULD WE ADD TO STATE INSTEAD
      getColumnPreferences(presetName);
      setLoading(false);
    } catch (e: any) {
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Always save the sort preference data in the 'default' preset to provide a consistent sort model across all presets
   * @param sortModel
   */
  const saveSortPreferenceData = async (sortModel: GridSortModel) => {
    try {
      const requestObject = {
        body: {
          ...columnPreferences,
          sortOrder: sortModel,
          presetName: "default",
        },
      };
      await API.post("crewlife", "/presetpreference/save", requestObject);
      setColumnPreferences({
        ...columnPreferences,
        sortOrder: sortModel,
        presetName: "default",
      });
    } catch (e: any) {
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  };

  return (
    <ColumnContext.Provider
      value={{
        loading,
        savePresets,
        getColumnPresets,
        getColumnPreferences,
        saveSortPreferenceData,
        columnPreferences,
        columnPresets,
      }}
    >
      {props.children}
    </ColumnContext.Provider>
  );
};

export const useColumnPresets = () => {
  return useContext(ColumnContext);
};
